import { action, observable, makeObservable } from 'mobx'
import SilentListStore from 'stores/SilentListStore'
import QuotesService from 'services/QuotesService'
import i18next from 'i18next'
import Paginator from 'models/Paginator'
import Sorter from 'models/Sorter'

class QuotesStore extends SilentListStore {
    DEFAULT_SORT_FIELD = 'date'
    DEFAULT_SORT_SENSE = 'DESC'
    ACTIVE_STATES = ['activated', 'pending', 'ended', 'in_emission', 'failed_emission']
    INACTIVE_STATES = ['expired', 'cancelled']
    quotes = []
    pages = ''
    currentPage = 1
    loading = false
    stateFilterSelected = 'active' // or expired
    paginator = Paginator.empty()
    sorter = Sorter.fromJson({
        sortField: this.DEFAULT_SORT_FIELD,
        sortSense: this.DEFAULT_SORT_SENSE,
    })

    statusOptions = [
        {
            id: `activated`,
            value: i18next.t(`quotes:actives`),
        },
        {
            id: `failed_emission`,
            value: i18next.t(`quotes:failedPlural`),
        },
        {
            id: `pending`,
            value: i18next.t(`quotes:pendings`),
        },
        {
            id: `ended`,
            value: i18next.t(`quotes:endedPlural`),
        },
        {
            id: `cancelled`,
            value: i18next.t(`quotes:cancelledPlural`),
        },
        {
            id: `expired`,
            value: i18next.t(`quotes:expiredPlural`),
        },
    ]

    optionTypeSelected = ''
    optionStatusSelected = ''
    nameTextSearch = ''
    filterSelectedBroker = null
    filterSelectedOrigin = null

    constructor() {
        super({ itemsKey: 'quotes', cacheExtraKeys: ['pages'] })

        this.makeObservables()

        this.quotesService = new QuotesService()
        this.orderOrientation = 1
        this.filterStates =
            this.stateFilterSelected === 'active' ? this.ACTIVE_STATES : this.INACTIVE_STATES
    }

    getListCacheKey() {
        return [
            this.stateFilterSelected,
            this.optionStatusSelected || '',
            this.optionTypeSelected || '',
            this.nameTextSearch || '',
            this.filterSelectedBroker || '',
            this.filterSelectedOrigin || '',
            this.currentPage,
            this.sorter.sortField,
            this.sorter.sortSense,
        ].join('|')
    }

    setDefaultSort() {
        this.setSort(this.DEFAULT_SORT_FIELD, this.DEFAULT_SORT_SENSE)
    }

    setStateFilter(state) {
        this.stateFilterSelected = state
        this.optionStatusSelected = ''
        this.filterStates =
            this.stateFilterSelected === 'active' ? this.ACTIVE_STATES : this.INACTIVE_STATES
    }

    static changeCheckedStatus(quote) {
        quote.changeCheckedStatus()
    }

    changePage(page) {
        this.currentPage = page
    }

    filterQuotesByStatus(optionStatus) {
        this.stateFilterSelected = 'active'
        this.optionStatusSelected = optionStatus

        if (optionStatus === null || optionStatus === '') {
            this.filterStates = this.ACTIVE_STATES
        } else if (optionStatus === 'expired') {
            this.stateFilterSelected = 'expired'
            this.filterStates = this.INACTIVE_STATES
        } else {
            this.filterStates = [optionStatus]
        }
    }

    filterQuotesByType(optionStatus) {
        if (optionStatus) {
            this.optionTypeSelected = optionStatus
        } else {
            this.optionTypeSelected = ''
        }
    }

    filterQuotesByBroker(broker) {
        this.filterSelectedBroker = broker
    }

    filterQuotesByOrigin(group) {
        this.filterSelectedOrigin = group
    }

    filterQuotesByClient(searchText) {
        this.nameTextSearch = searchText
    }

    // eslint-disable-next-line class-methods-use-this
    compareQuoteDate(orderOrientation) {
        return (a, b) => {
            const dateA = a.date
            const dateB = b.date

            let comparison = 0
            if (dateA > dateB) {
                comparison = 1
            } else if (dateA < dateB) {
                comparison = -1
            }
            return comparison * orderOrientation
        }
    }

    orderByDate() {
        this.orderOrientation *= -1
        this.quotes = this.quotes.sort(this.compareQuoteDate(this.orderOrientation))
    }

    async listQuotes({ silent = false } = {}) {
        await this.fetchList({
            silent,
            onRetry: () => this.listQuotes(),
            request: async () => {
                let { sortField } = this.sorter

                if (sortField === 'displayName') {
                    sortField = 'customer'
                }

                const params = {
                    page: this.currentPage,
                    multiQuoteStatuses: this.filterStates,
                    type: this.optionTypeSelected,
                    search: this.nameTextSearch,
                    broker: this.filterSelectedBroker ? this.filterSelectedBroker : null,
                    group: this.filterSelectedOrigin ? this.filterSelectedOrigin : null,
                    [`sort[${sortField}]`]: this.sorter.sortSense,
                }
                const response = await this.quotesService.getQuotes(params)

                return {
                    items: response.quotes,
                    paginator: response.paginator,
                    pages: response.pagination?.pages ?? '',
                }
            },
        })
    }

    changeSort(sortField) {
        this.sorter.changeSort(sortField)
    }

    setSort(sortField, sortSense) {
        if (sortField && sortSense) {
            this.sorter.setSort(sortField, sortSense)
        } else {
            this.setDefaultSort()
        }
    }

    makeObservables() {
        makeObservable(this, {
            // observables
            quotes: observable,
            pages: observable,
            currentPage: observable,
            loading: observable,
            stateFilterSelected: observable,
            paginator: observable,
            sorter: observable,
            statusOptions: observable,
            optionTypeSelected: observable,
            optionStatusSelected: observable,
            nameTextSearch: observable,
            filterSelectedBroker: observable,
            filterSelectedOrigin: observable,
            // actions
            setStateFilter: action,
            changePage: action,
            filterQuotesByStatus: action,
            filterQuotesByType: action,
            filterQuotesByClient: action,
            filterQuotesByBroker: action,
            filterQuotesByOrigin: action,
            compareQuoteDate: action,
            orderByDate: action,
            listQuotes: action,
            changeSort: action,
            setSort: action,
            ...this.getSilentListAnnotations(),
        })
    }
}

export default QuotesStore
